// FieldVision Admin - API Service v5
// Merged: email tracking (v4) + pris-redigering (claude-work)

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';

// Token håndtering
function getToken(): string | null {
  return localStorage.getItem('fieldvision_token');
}

function setToken(token: string): void {
  localStorage.setItem('fieldvision_token', token);
}

function clearToken(): void {
  localStorage.removeItem('fieldvision_token');
  localStorage.removeItem('fieldvision_user');
}

function getUser(): any {
  const userStr = localStorage.getItem('fieldvision_user');
  return userStr ? JSON.parse(userStr) : null;
}

function setUser(user: any): void {
  localStorage.setItem('fieldvision_user', JSON.stringify(user));
}

// Fetch wrapper med auth header
async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const token = getToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  // Ved 401/403, clear token og redirect til login
  if (response.status === 401 || response.status === 403) {
    clearToken();
    window.location.href = '/';
  }

  return response;
}

// ============================================================================
// Auth
// ============================================================================

export async function login(email: string, password: string): Promise<{ token: string; user: any }> {
  const response = await fetch(`${API_BASE}/api/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Login fejlede');
  }

  // Gem token og user
  setToken(data.token);
  setUser(data.user);

  return data;
}

export function logout(): void {
  clearToken();
  window.location.href = '/';
}

export function isAuthenticated(): boolean {
  return !!getToken();
}

export function getCurrentUser(): any {
  return getUser();
}

// ============================================================================
// Orders
// ============================================================================

export interface Order {
  orderId: number;
  orderNumber: string;
  userId: number;
  customerEmail: string;
  customerName: string;
  customerPhone: string;
  customerCVR: string;
  mapType: string;
  source: string;
  totalArea: number;
  pricePerHa: number;
  totalPrice: number;
  // Pris-redigering felter (v5)
  calculatedPrice?: number;
  discountAmount?: number;
  discountReason?: string;
  status: string;
  pilotId: number | null;
  pilotName: string | null;
  // Email tracking felter (v4)
  customerEmailSentAt?: string | null;
  customerConfirmedAt: string | null;
  pilotEmailSentAt?: string | null;
  pilotConfirmedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  fieldCount: number;
  mapCount: number;
}

export interface OrderField {
  id: number;
  markblokId: string;
  marknr: string;
  areal: number;
  geometry: any;
}

export interface OrderHistory {
  historyId: number;
  oldStatus: string;
  newStatus: string;
  changedBy: string;
  pilotId: number | null;
  pilotName: string | null;
  note: string;
  createdAt: string;
}

export interface OrderDetail {
  order: Order;
  fields: OrderField[];
  history: OrderHistory[];
}

export interface OrdersResponse {
  orders: Order[];
  statusCounts: Record<string, number>;
}

export async function getOrders(status?: string): Promise<OrdersResponse> {
  const url = status ? `/api/admin/orders?status=${encodeURIComponent(status)}` : '/api/admin/orders';
  const response = await fetchWithAuth(url);

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Kunne ikke hente ordrer');
  }

  return response.json();
}

export async function getOrder(orderId: number): Promise<OrderDetail> {
  const response = await fetchWithAuth(`/api/admin/orders/${orderId}`);

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Kunne ikke hente ordre');
  }

  return response.json();
}

export async function sendCustomerConfirmation(orderId: number): Promise<any> {
  const response = await fetchWithAuth(`/api/admin/orders/${orderId}/send-customer`, {
    method: 'POST',
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Kunne ikke sende bekræftelse');
  }

  return response.json();
}

export async function assignPilot(orderId: number, pilotId: number): Promise<any> {
  const response = await fetchWithAuth(`/api/admin/orders/${orderId}/assign-pilot`, {
    method: 'POST',
    body: JSON.stringify({ pilotId }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Kunne ikke tildele pilot');
  }

  return response.json();
}

// v5: Opdater ordre pris (til rabat)
export async function updateOrderPrice(orderId: number, price: number, reason?: string): Promise<void> {
  const response = await fetchWithAuth(`/api/admin/orders/${orderId}/price`, {
    method: 'PUT',
    body: JSON.stringify({ price, reason }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Kunne ikke opdatere pris');
  }
}

// ============================================================================
// Pilots
// ============================================================================

export interface Pilot {
  pilotId: number;
  name: string;
  email: string;
  phone: string;
  address: string;
  postalCode: string;
  city: string;
  latitude: number | null;
  longitude: number | null;
  droneType: string;
  isActive: boolean;
  distance?: number;
}

export async function getPilots(): Promise<{ pilots: Pilot[] }> {
  const response = await fetchWithAuth('/api/admin/pilots');

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Kunne ikke hente piloter');
  }

  return response.json();
}

export async function getPilotsNearest(lat: number, lon: number): Promise<{ pilots: Pilot[] }> {
  const response = await fetchWithAuth(`/api/admin/pilots/nearest?lat=${lat}&lon=${lon}`);

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Kunne ikke hente piloter');
  }

  return response.json();
}

export async function createPilot(pilot: Partial<Pilot>): Promise<any> {
  const response = await fetchWithAuth('/api/admin/pilots', {
    method: 'POST',
    body: JSON.stringify(pilot),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Kunne ikke oprette pilot');
  }

  return response.json();
}

export async function updatePilot(pilotId: number, pilot: Partial<Pilot>): Promise<any> {
  const response = await fetchWithAuth(`/api/admin/pilots/${pilotId}`, {
    method: 'PUT',
    body: JSON.stringify(pilot),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Kunne ikke opdatere pilot');
  }

  return response.json();
}

export async function deletePilot(pilotId: number): Promise<any> {
  const response = await fetchWithAuth(`/api/admin/pilots/${pilotId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Kunne ikke slette pilot');
  }

  return response.json();
}

export default {
  login,
  logout,
  isAuthenticated,
  getCurrentUser,
  getOrders,
  getOrder,
  sendCustomerConfirmation,
  assignPilot,
  updateOrderPrice,  // v5
  getPilots,
  getPilotsNearest,
  createPilot,
  updatePilot,
  deletePilot,
};

// ============================================================================
// User Types (User Admin Module v1.0)
// ============================================================================

export interface User {
  UserId: number;
  Email: string;
  Name: string;
  Phone: string;
  CompanyName: string;
  Address: string;
  PostalCode: string;
  City: string;
  CVR: string;
  SubscriptionTier: 'Basis' | 'Pro' | 'Advanced';
  IsActive: boolean;
  IsAdmin: boolean;
  CreatedAt: string;
  LastLogin: string | null;
  CurrentMonthUsage?: number;
  MonthlyLimit?: number;
}

export interface UserQuota {
  used: number;
  limit: number;
  yearMonth: string;
}

export interface UserDetailResponse {
  user: User;
  cvrs: string[];
  quota: UserQuota;
}

export interface UsersResponse {
  users: User[];
}

export interface ResetQuotaResponse {
  success: boolean;
  message: string;
  previousCount: number;
  newCount: number;
  yearMonth: string;
}

export interface TempPasswordResponse {
  success: boolean;
  tempPassword: string;
  message: string;
  email: string;
}

// ============================================================================
// User API Functions (User Admin Module v1.0)
// ============================================================================

export async function getUsers(search?: string): Promise<UsersResponse> {
  const url = search
    ? `/api/admin/users?search=${encodeURIComponent(search)}`
    : '/api/admin/users';

  const response = await fetchWithAuth(url);

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Kunne ikke hente brugere');
  }

  return response.json();
}

export async function getUserById(id: number): Promise<UserDetailResponse> {
  const response = await fetchWithAuth(`/api/admin/users/${id}`);

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Kunne ikke hente bruger');
  }

  return response.json();
}

export async function updateUser(id: number, data: Partial<User>): Promise<{ success: boolean; user: User }> {
  const response = await fetchWithAuth(`/api/admin/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const responseData = await response.json();
    throw new Error(responseData.error || 'Kunne ikke opdatere bruger');
  }

  return response.json();
}

export async function resetUserQuota(id: number): Promise<ResetQuotaResponse> {
  const response = await fetchWithAuth(`/api/admin/users/${id}/reset-quota`, {
    method: 'POST',
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Kunne ikke nulstille kvota');
  }

  return response.json();
}

export async function sendPasswordResetEmail(id: number): Promise<{ success: boolean; message: string }> {
  const response = await fetchWithAuth(`/api/admin/users/${id}/send-reset-email`, {
    method: 'POST',
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Kunne ikke sende reset email');
  }

  return response.json();
}

export async function setTempPassword(id: number): Promise<TempPasswordResponse> {
  const response = await fetchWithAuth(`/api/admin/users/${id}/set-temp-password`, {
    method: 'POST',
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Kunne ikke saette midlertidigt password');
  }

  return response.json();
}

// Opret ny bruger (admin)
export async function createUser(userData: {
  Email: string;
  Name: string;
  Phone?: string;
  CVR?: string;
  CompanyName?: string;
}): Promise<{ success: boolean; user: User; tempPassword: string }> {
  const response = await fetchWithAuth('/api/admin/users', {
    method: 'POST',
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Kunne ikke oprette bruger');
  }

  return response.json();
}

// ============================================================================
// User Fields and Maps Upload (Admin Upload Module)
// ============================================================================

export interface UserField {
  marknr: string;
  afgkode: string;
  areal: number;
}

export interface UserFieldsResponse {
  cvr: string;
  fields: UserField[];
}

export interface MapUploadResult {
  filename: string;
  success: boolean;
  error: string | null;
  mapId: number | null;
  marknr?: string;
  mapType?: string;
}

export interface MapsUploadResponse {
  success: boolean;
  message: string;
  results: MapUploadResult[];
}

// Hent brugers marker for et CVR
export async function getUserFields(userId: number, cvr: string): Promise<UserFieldsResponse> {
  const response = await fetchWithAuth(`/api/admin/users/${userId}/fields/${encodeURIComponent(cvr)}`);

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Kunne ikke hente marker');
  }

  return response.json();
}

// Upload tildelingskort til bruger
export async function uploadUserMaps(userId: number, cvr: string, files: File[]): Promise<MapsUploadResponse> {
  const token = getToken();
  const formData = new FormData();
  
  formData.append('cvr', cvr);
  files.forEach((file) => {
    formData.append('files', file);
  });

  const response = await fetch(`${API_BASE}/api/admin/users/${userId}/maps`, {
    method: 'POST',
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
    body: formData,
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Upload fejlede');
  }

  return response.json();
}
